import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { corMedida, formatarNumero, rotuloCurtoMes, temaGrafico } from "./analiseUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/** Valor escrito acima de cada barra, como no modelo. */
const valorNoTopo = {
    id: "valorNoTopo",
    afterDatasetsDraw(chart, args, opcoes) {
        const { ctx } = chart;
        ctx.save();
        ctx.font = "600 11px Roboto, Helvetica, Arial, sans-serif";
        ctx.fillStyle = opcoes.cor;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        chart.data.datasets.forEach((dataset, di) => {
            const meta = chart.getDatasetMeta(di);
            if (meta.hidden) return;
            meta.data.forEach((barra, i) => {
                const valor = dataset.data[i];
                if (!valor) return;
                const { x, y } = barra.getProps(["x", "y"], true);
                ctx.fillText(formatarNumero(valor), x, y - 4);
            });
        });
        ctx.restore();
    },
};

/**
 * Evolução mês a mês: fichas acionadas e atendimentos (barras agrupadas com o valor no topo).
 * As duas medidas são contagens e dividem o mesmo eixo.
 */
export default function GraficoEvolucao({ meses, fichasMes, atendimentosMes }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);

    const serie = (rotulo, dados, medida) => ({
        label: rotulo,
        data: dados,
        backgroundColor: corMedida(medida, darkMode),
        borderColor: tema.superficie,
        borderWidth: { left: 1, right: 1 },
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: "bottom",
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        maxBarThickness: 42,
    });

    const maximo = Math.max(...fichasMes, ...atendimentosMes, 1);

    const data = {
        labels: meses.map(rotuloCurtoMes),
        datasets: [
            serie("Fichas acionadas", fichasMes, "fichas"),
            serie("Atendimentos", atendimentosMes, "atendimentos"),
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        layout: { padding: { top: 8 } },
        plugins: {
            legend: {
                position: "top",
                align: "center",
                labels: { color: tema.texto, usePointStyle: true, pointStyle: "circle", boxWidth: 8, boxHeight: 8, padding: 16, font: { size: 12 } },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${formatarNumero(ctx.parsed.y)}`,
                },
            },
            valorNoTopo: { cor: tema.texto },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { color: tema.grade },
                ticks: { color: tema.textoSecundario, font: { size: 12 } },
            },
            y: {
                beginAtZero: true,
                suggestedMax: maximo * 1.15, // espaço para o valor no topo das barras
                grid: { color: tema.grade },
                border: { display: false },
                ticks: { color: tema.textoSecundario, font: { size: 11 }, callback: (v) => formatarNumero(v) },
            },
        },
    };

    return (
        <MDBox height="290px">
            <Bar data={data} options={options} plugins={[valorNoTopo]} />
        </MDBox>
    );
}
